import { useCallback, useEffect, useRef, useState } from "react";

interface ABIItem {
  type: string;
  name?: string;
  inputs?: Array<{
    name: string;
    type: string;
    indexed?: boolean;
  }>;
  outputs?: unknown[];
  stateMutability?: string;
  anonymous?: boolean;
}

interface EventABI extends ABIItem {
  type: "event";
  name: string;
}

interface UseContractABIOptions {
  chainId?: number;
  address?: string;
  enabled?: boolean;
  debounceMs?: number;
}

interface ContractABIResult {
  abi: ABIItem[] | null;
  events: EventABI[];
  isLoading: boolean;
  error: string | null;
  status: "idle" | "loading" | "success" | "error" | "not-found";
  refetch: () => Promise<void>;
  contractName: string | null;
  nameTag: string | null;
  isProxy: boolean;
  proxyContractName: string | null;
  implementationAddress: string | null;
  warning: string | null;
}

/**
 * Hook to fetch contract ABI from the contract-abi-api
 * Includes automatic debouncing and caching
 */
export function useContractABI({
  chainId,
  address,
  enabled = true,
  debounceMs = 800,
}: UseContractABIOptions): ContractABIResult {
  const [abi, setAbi] = useState<ABIItem[] | null>(null);
  const [events, setEvents] = useState<EventABI[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [status, setStatus] = useState<"idle" | "loading" | "success" | "error" | "not-found">("idle");
  const [contractName, setContractName] = useState<string | null>(null);
  const [nameTag, setNameTag] = useState<string | null>(null);
  const [isProxy, setIsProxy] = useState(false);
  const [proxyContractName, setProxyContractName] = useState<string | null>(null);
  const [implementationAddress, setImplementationAddress] = useState<string | null>(null);
  const [warning, setWarning] = useState<string | null>(null);

  const fetchTimeoutRef = useRef<NodeJS.Timeout | undefined>(undefined);
  const lastFetchKey = useRef<string>("");

  const fetchABI = useCallback(async () => {
    if (!chainId || !address || !enabled) {
      return;
    }

    const fetchKey = `${chainId}-${address}`;
    if (lastFetchKey.current === fetchKey && abi) {
      return; // Already fetched
    }

    lastFetchKey.current = fetchKey;
    setIsLoading(true);
    setStatus("loading");
    setError(null);

    try {
      console.log(`[useContractABI] Fetching ABI for ${address} on chain ${chainId}`);

      const response = await fetch(`/api/contract-abi?chainId=${chainId}&address=${address}`);
      const data = await response.json();

      if (data.success && data.abi) {
        const parsedAbi: ABIItem[] = typeof data.abi === "string" ? JSON.parse(data.abi) : data.abi;
        const eventAbis = parsedAbi.filter((item): item is EventABI => item.type === "event" && !!item.name);

        setAbi(parsedAbi);
        setEvents(eventAbis);
        setContractName(data.contractName || null);
        setNameTag(data.nameTag || null);
        setIsProxy(!!data.isProxy);
        setProxyContractName(data.proxyContractName || null);
        setImplementationAddress(data.implementationAddress || null);
        setWarning(data.warning || null);
        setStatus("success");
        console.log(
          `[useContractABI] Success - found ${eventAbis.length} events, contractName: ${data.contractName || "N/A"}${data.isProxy ? ` (proxy: ${data.proxyContractName} → impl: ${data.implementationAddress})` : ""}`,
        );
      } else {
        setStatus("not-found");
        setError(data.error || "Contract not verified on any explorer");
        setContractName(null);
        setNameTag(null);
        setIsProxy(false);
        setProxyContractName(null);
        setImplementationAddress(null);
        setWarning(null);
        console.log(`[useContractABI] Not found: ${data.error}`);
      }
    } catch (err) {
      console.error("[useContractABI] Error:", err);
      setStatus("error");
      setError(err instanceof Error ? err.message : "Failed to fetch ABI");
    } finally {
      setIsLoading(false);
    }
  }, [chainId, address, enabled, abi]);

  // Clear cached data when chainId or address changes
  useEffect(() => {
    // Reset state when inputs change
    setAbi(null);
    setEvents([]);
    setContractName(null);
    setNameTag(null);
    setIsProxy(false);
    setProxyContractName(null);
    setImplementationAddress(null);
    setWarning(null);
    setStatus("idle");
    setError(null);
    lastFetchKey.current = "";
  }, [chainId, address]);

  // Auto-fetch with debouncing
  useEffect(() => {
    if (!enabled || !chainId || !address) {
      // Clear data if disabled or missing inputs
      setAbi(null);
      setEvents([]);
      setContractName(null);
      setNameTag(null);
      setIsProxy(false);
      setProxyContractName(null);
      setImplementationAddress(null);
      setWarning(null);
      setStatus("idle");
      setError(null);
      return;
    }

    // Clear previous timeout
    if (fetchTimeoutRef.current) {
      clearTimeout(fetchTimeoutRef.current);
    }

    // Debounce the fetch
    fetchTimeoutRef.current = setTimeout(() => {
      fetchABI();
    }, debounceMs);

    return () => {
      if (fetchTimeoutRef.current) {
        clearTimeout(fetchTimeoutRef.current);
      }
    };
  }, [chainId, address, enabled, debounceMs, fetchABI]);

  return {
    abi,
    events,
    isLoading,
    error,
    status,
    refetch: fetchABI,
    contractName,
    nameTag,
    isProxy,
    proxyContractName,
    implementationAddress,
    warning,
  };
}
